import React, { 
    useState, 
    useEffect, 
    useCallback, 
    useMemo
} from 'react';
import './SalesPurchases.css'; // Import custom CSS for additional styling
import { FaPlus, FaTrash, FaChartLine, FaMoneyBillWave, FaShoppingCart } from 'react-icons/fa';
import { stockApi } from '../services/api';

const SalesPurchases = () => {
    // Use ref to track first render and prevent unnecessary re-renders
    // const isFirstRender = useRef(true);
    // const mountCountRef = useRef(0);

    // State management
    const [transactions, setTransactions] = useState([]);
    const [newTransaction, setNewTransaction] = useState({ type: '', amount: 0 });
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    // Fetch transactions when component mounts
    useEffect(() => {
        fetchTransactions();
    }, []);

    // Fetch transactions from API
    const fetchTransactions = async () => {
        try {
            setLoading(true);
            const response = await stockApi.getAll();
            // Filter for only sales and purchase transactions
            const salesPurchases = Array.isArray(response.data) 
                ? response.data.filter(t => t.action === 'Sale' || t.action === 'Purchase')
                : [];
            setTransactions(salesPurchases);
            setError(null);
        } catch (err) {
            console.error('Error fetching transactions:', err);
            setError('Failed to fetch transactions. Please try again later.');
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    };

    // Memoized transaction addition with enhanced validation
    const addTransaction = useCallback(async () => {
        try {
            // Strict validation
            if (!newTransaction.type || newTransaction.amount <= 0) {
                console.warn('Invalid transaction:', newTransaction);
                return;
            }

            const newTransactionEntry = { 
                productId: 0, // This will be handled by the backend
                type: newTransaction.type,
                quantity: Number(newTransaction.amount),
                reason: 'Manual transaction',
                transactionDate: new Date().toISOString(),
                unitPrice: 0, // Default value
                totalAmount: 0 // Default value
            };

            console.log('Sending transaction to database:', {
                timestamp: new Date().toISOString(),
                transactionData: newTransactionEntry,
                rawInput: newTransaction
            });

            await stockApi.create(newTransactionEntry);
            
            // Refresh transactions list
            await fetchTransactions();
            
            // Reset transaction input
            setNewTransaction({ type: '', amount: 0 });
            
            console.log('Transaction successfully added to database');
        } catch (err) {
            console.error('Transaction addition error:', {
                error: err,
                response: err.response?.data,
                timestamp: new Date().toISOString()
            });
            setError(err.message);
        }
    }, [newTransaction]);

    // Memoized total calculation with error handling
    const transactionSummary = useMemo(() => {
        try {
            const validTransactions = transactions.filter(t => t && t.action && t.amount);
            
            const totalSales = validTransactions
                .filter(t => t.action === 'Sale')
                .reduce((sum, t) => sum + Number(t.amount), 0);
            
            const totalPurchases = validTransactions
                .filter(t => t.action === 'Purchase')
                .reduce((sum, t) => sum + Number(t.amount), 0);

            return {
                totalTransactions: validTransactions.length,
                totalSales,
                totalPurchases,
                netBalance: totalSales - totalPurchases
            };
        } catch (error) {
            console.error('Error calculating transaction summary:', error);
            return {
                totalTransactions: 0,
                totalSales: 0,
                totalPurchases: 0,
                netBalance: 0
            };
        }
    }, [transactions]);

    // Handlers with useCallback and additional validation
    const handleTypeChange = useCallback((e) => {
        const type = e.target.value;
        setNewTransaction(prev => ({ 
            ...prev, 
            type: ['in', 'out'].includes(type) ? type : '' 
        }));
    }, []);

    const handleAmountChange = useCallback((e) => {
        const amount = Number(e.target.value);
        setNewTransaction(prev => ({ 
            ...prev, 
            amount: !isNaN(amount) && amount >= 0 ? amount : 0 
        }));
    }, []);

    // Clear transactions with confirmation
    const clearTransactions = useCallback(async () => {
        const confirmClear = window.confirm('Are you sure you want to clear all transactions?');
        if (confirmClear) {
            try {
                // In a real app, you would have an API endpoint to clear transactions
                // For now, we'll just refresh the list
                await fetchTransactions();
            } catch (err) {
                console.error('Error clearing transactions:', err);
                setError('Failed to clear transactions. Please try again.');
            }
        }
    }, []);

    if (loading) {
        return <div className="loading">Loading transactions...</div>;
    }

    if (error) {
        return (
            <div className="error-container">
                <h2>Transaction Error</h2>
                <p>{error}</p>
                <button 
                    className="btn btn-danger" 
                    onClick={() => setError(null)}
                >
                    Reset
                </button>
            </div>
        );
    }

    return (
        <div className="sales-purchases">
            <h2>Sales & Purchases Tracker</h2>
            
            <div className="row">
                <div className="col-12">
                    {/* Transaction Summary Card */}
                    <div className="card mb-4 shadow-sm">
                        <div className="card-body">
                            <h3 className="card-title"><FaChartLine className="me-2" /> Financial Overview</h3>
                            <div className="row text-center">
                                <div className="col-md-4 mb-3 mb-md-0">
                                    <div className="summary-item">
                                        <small className="text-muted">Total Transactions</small>
                                        <h4>{transactionSummary.totalTransactions}</h4>
                                    </div>
                                </div>
                                <div className="col-md-4 mb-3 mb-md-0">
                                    <div className="summary-item">
                                        <small className="text-success">Total Sales</small>
                                        <h4>${transactionSummary.totalSales.toFixed(2)}</h4>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="summary-item">
                                        <small className="text-danger">Total Purchases</small>
                                        <h4>${transactionSummary.totalPurchases.toFixed(2)}</h4>
                                    </div>
                                </div>
                            </div>
                            <hr />
                            <div className="text-center">
                                <h5>
                                    Net Balance: 
                                    <span className={`badge ms-2 ${
                                        transactionSummary.netBalance >= 0 
                                            ? 'bg-success' 
                                            : 'bg-danger'
                                    }`}>
                                        ${Math.abs(transactionSummary.netBalance).toFixed(2)}
                                    </span>
                                </h5>
                            </div>
                        </div>
                    </div>

                    {/* Transaction Input Card */}
                    <div className="card mb-4 shadow-sm">
                        <div className="card-body">
                            <h3 className="card-title"><FaPlus className="me-2" /> Add Transaction</h3>
                            <div className="row">
                                <div className="col-12 col-md-6 mb-3">
                                    <label htmlFor="transactionType" className="form-label">Transaction Type</label>
                                    <select 
                                        id="transactionType"
                                        className="form-control" 
                                        value={newTransaction.type} 
                                        onChange={handleTypeChange}
                                    >
                                        <option value="">Select Type</option>
                                        <option value="in">Purchase</option>
                                        <option value="out">Sale</option>
                                    </select>
                                </div>
                                <div className="col-12 col-md-6 mb-3">
                                    <label htmlFor="transactionAmount" className="form-label">Amount ($)</label>
                                    <input 
                                        id="transactionAmount"
                                        type="number" 
                                        className="form-control" 
                                        placeholder="0.00" 
                                        value={newTransaction.amount || ''} 
                                        onChange={handleAmountChange}
                                    />
                                </div>
                            </div>
                            <div className="d-grid gap-2">
                                <button 
                                    className="btn btn-primary" 
                                    onClick={addTransaction}
                                    disabled={!newTransaction.type || !newTransaction.amount}
                                >
                                    {newTransaction.type === 'out' ? <FaMoneyBillWave className="me-2" /> : 
                                     newTransaction.type === 'in' ? <FaShoppingCart className="me-2" /> : 
                                     <FaPlus className="me-2" />}
                                    Add {newTransaction.type || 'Transaction'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Transactions List Card */}
                    <div className="card shadow-sm">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h3 className="card-title mb-0">Transaction History</h3>
                                <button 
                                    className="btn btn-danger" 
                                    onClick={clearTransactions}
                                >
                                    <FaTrash className="me-2" /> Clear All
                                </button>
                            </div>
                            
                            {Array.isArray(transactions) && transactions.length > 0 ? (
                                <div className="table-responsive">
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Type</th>
                                                <th>Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {transactions.map((transaction) => (
                                                <tr key={transaction.id}>
                                                    <td>{new Date(transaction.date).toLocaleString()}</td>
                                                    <td>
                                                        <span className={`badge ${
                                                            transaction.action === 'out' 
                                                                ? 'bg-success' 
                                                                : 'bg-danger'
                                                        }`}>
                                                            {transaction.action}
                                                        </span>
                                                    </td>
                                                    <td>${transaction.amount.toFixed(2)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <p className="text-muted mb-0">No transactions recorded yet.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SalesPurchases; 